import { html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { ViewBase } from '../view-base.js';
import { adminMembersViewStyles } from './admin-members-view-styles.js';

import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/tree/tree.js';
import '@shoelace-style/shoelace/dist/components/tree-item/tree-item.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';

import { q1AdminMemberFunctions } from '../../modules/q1-admin-functions.js';
import { q1MemberFunctions } from '../../modules/q1-core-functions.js'
import { FormHostMixin } from '../../modules/form-host-mixin.js';


const DEFAULT_PROFILE_COLOR_PRIMARY = '#E2B013';
const LEVEL_ICONS = {
  network: 'graph_5',
  community: 'graph_5',
  contributor: 'communities',
  foundation: 'foundation',
  steward: 'crowdsource',
};

const SORT_BY_OPTIONS = [
  { label: 'Number', value: 'number' },
  { label: 'Name', value: 'nameInternal' },
  { label: 'Created At', value: 'createdAt' },
];
const SORT_BY_DEFAULT = 'nameInternal';

const SORT_DIRECTION_OPTIONS = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' },
];
const SORT_DIRECTION_DEFAULT = 'asc';

const PAGE_SIZE_OPTIONS = [
  { label: '5', value: '5' }, // FIXME: Delete
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
];
const PAGE_SIZE_DEFAULT = 100;

const FILTER_BY_LEVELS_OPTIONS = [
  { label: 'Network', value: 'network', icon: LEVEL_ICONS['network'] },
  { label: 'Contributor', value: 'contributor', icon: LEVEL_ICONS['contributor'] },
  { label: 'Foundation', value: 'foundation', icon: LEVEL_ICONS['foundation'] },
  { label: 'Steward', value: 'steward', icon: LEVEL_ICONS['steward'] },
];
const FILTER_BY_LEVELS_DEFAULT = [];

const FILTER_BY_STATUS_OPTIONS = [
  { label: 'Provisioned', value: 'provisioned' },
  { label: 'Registered', value: 'registered' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Onboarded', value: 'onboarded' },
  { label: 'Deactivated', value: 'deactivated' },
  { label: 'Deleted', value: 'deleted' },
];
const FILTER_BY_STATUS_DEFAULT = [];

/**
 * ## Admin Members View ##
 * 
 * URL Params:
 * - sort_by = number, nameInternal, createdAt
 * - order = asc, desc
 * - levels = Comma-delimited list of: network, contributor, foundation, steward
 * - status = Comma-delimited list of: provisioned, registered, confirmed, onboarded, deactivated, deleted
 */
export class AdminMembersView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    adminMembersViewStyles
  ];

  static properties = {
    ...super.properties,
    
    members: { type: Array },
    memberCount: { type: Number }, // ALL members, regardless of query

    sortBy: { type: String },
    sortDirection: { type: String },
    pageSizeString: { type: String }, // used to convert to number value
    pageSize: { type: Number },
    pageHistory: { type: Array },

    querySize: { type: Number },
    firstItemIndex: { type: Number },
    lastItemIndex: { type: Number },

    showSettings: { type: Boolean }, // only used on mobile views

    filterByLevels: { type: Array },
    filterByStatus: { type: Array },
    
    priorPage: { type: String },
    nextPage: { type: String },
    
    loading: { type: Boolean }
  };

  constructor() {
    super();

    this.loading = true; // prevents auto-query until initView()
    
    this.members = [];
    this.memberCount = 0;
    
    this.sortBy = SORT_BY_OPTIONS.map(o => o.value).includes(this.urlParams.sort_by)
      ? this.urlParams.sort_by
      : SORT_BY_DEFAULT;
    this.sortDirection = SORT_DIRECTION_OPTIONS.map(o => o.value).includes(this.urlParams.order)
      ? this.urlParams.order
      : SORT_DIRECTION_DEFAULT;
    this.pageSizeString = PAGE_SIZE_DEFAULT.toString();
    this.pageSize = PAGE_SIZE_DEFAULT;
    this.pageHistory = [];

    this.querySize = 0;
    this.firstItemIndex = 0;
    this.lastItemIndex = 0;

    this.showSettings = false;

    // Note: This one needs manual population (in initView) if set from urlParams
    const levelValues = FILTER_BY_LEVELS_OPTIONS.map(o => o.value);
    this.filterByLevels = this.urlParams.levels?.length
      ? this.urlParams.levels.split(',').filter(l => levelValues.includes(l))
      : FILTER_BY_LEVELS_DEFAULT;

    const statusValues = FILTER_BY_STATUS_OPTIONS.map(o => o.value);
    this.filterByStatus = this.urlParams.status?.length
      ? this.urlParams.status.split(',').filter(s => statusValues.includes(s))
      : FILTER_BY_STATUS_DEFAULT;
    
    this.priorPage = null;
    this.nextPage = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();

    this.initView();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (!this.loading) {
      if (changedProperties.has('routeParams')) {
        // Since the member index doesn't link to itself the only way to get here is to click the 
        // members link in the app header bar, which should just reset the query.
        this.resetQuery();
        return;
      }
    
      const QUERY_PARAMS = [
        'sortBy',
        'sortDirection',
        'pageSize',
        'filterByLevels',
        'filterByStatus',
      ];
      let queryParamsChanged = false;
      for (const param of QUERY_PARAMS) {
        if (changedProperties.has(param)) {
          queryParamsChanged = true;
          break;
        }
      }

      if (queryParamsChanged) this.queryMembers(); // queries page 1
    }
  }

  initView() {
    const { getAppStats } = q1MemberFunctions;

    getAppStats().then((response) => {
      const responseData = response.data || {};
      this.memberCount = responseData.memberCount;
    }).catch((error) => {
      console.log('queryAppStats: Error querying app stats', error);
    });

    this.queryMembers();
  }

  resetQuery() {
    this.loading = true; // prevents auto-query until done
    
    this.members = [];
    
    this.sortBy = SORT_BY_DEFAULT;
    this.sortDirection = SORT_DIRECTION_DEFAULT;
    this.pageSizeString = PAGE_SIZE_DEFAULT.toString();
    this.pageSize = PAGE_SIZE_DEFAULT;
    this.pageHistory = [];

    this.showSettings = false;

    this.filterByLevels = FILTER_BY_LEVELS_DEFAULT;
    this.filterByStatus = FILTER_BY_STATUS_DEFAULT;
    
    this.priorPage = null;
    this.nextPage = null;

    this.queryMembers();
  }

  _updateUrlParamsFromQuery() {
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();
    
    if (this.sortBy !== SORT_BY_DEFAULT) newParams.set('sort_by', this.sortBy);
    if (this.sortDirection !== SORT_DIRECTION_DEFAULT) newParams.set('order', this.sortDirection);
    if (this.filterByLevels.length > 0) newParams.set('levels', this.filterByLevels.join(','));
    if (this.filterByStatus.length > 0) newParams.set('status', this.filterByStatus.join(','));

    // Update the url if we have non-default params OR if we need to clear out prior params
    const newUrl = newParams.size > 0
      ? `${window.location.pathname}?${newParams.toString()}`
      : window.location.pathname;
    history.pushState(null, null, newUrl);
  }

  /**
   * The query page parameter should only be used when none of the other parameters have
   * changed. If they have changed then the cursors need to be reset and you have to go
   * back to page 1.
   */
  queryMembers(queryPage = 1) {
    if (!this.isSignedIn) {
      // unavailable when signed out
      this.loading = false;
      return;
    }

    const { adminBrowseMembers } = q1AdminMemberFunctions;

    this.loading = true;

    adminBrowseMembers({
      queryPage: queryPage,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      pageSize: this.pageSize,
      pageHistory: this.pageHistory,
      filterByLevels: this.filterByLevels,
      filterByStatus: this.filterByStatus,
    }).then((response) => {
      const {
        members,
        pageHistory, priorPage, nextPage,
        querySize, firstItemIndex, lastItemIndex,
      } = response.data || {};
      
      this.pageHistory = pageHistory;
      this.priorPage = priorPage;
      this.nextPage = nextPage;

      this.querySize = querySize;
      this.firstItemIndex = firstItemIndex;
      this.lastItemIndex = lastItemIndex;

      this.members = members.map(member => {
        // Title case the membership level field, also set the icon
        const level = member.level || 'network';
        member.levelIcon = LEVEL_ICONS[level] || LEVEL_ICONS['network'];
        member.level = level[0].toUpperCase() + level.substring(1);

        // Set the primary color if missing
        if (!member.colorPrimary) member.colorPrimary = DEFAULT_PROFILE_COLOR_PRIMARY;

        member.status = member.status || 'unknown';
        member.email = member.emailPrimary || '-';
        member.slack = member.slack || '-';

        return member;
      });

      this._updateUrlParamsFromQuery();
      this.loading = false;
    }).catch((error) => {
      console.log('queryMembers: Error querying members', error);
      this.showToast(error.message, 'Error Querying Members', 'danger');
      this.loading = false;
    });
  }

  toggleQuerySettings() {
    this.showSettings = !this.showSettings;
  }

  /*=== INPUT HANDLING ===*/

  get handleInputPostProcess() {
    return {
      pageSizeString: (newValue) => { this.pageSize = parseInt(newValue) || PAGE_SIZE_DEFAULT },
      filterByLevels: (newValue) => {
      },
      filterByStatus: (newValue) => {
      },
    };
  }

  /*=== BUTTON ACTIONS ===*/

  queryPriorPage() {
    if (this.priorPage) this.queryMembers(this.priorPage);
  }

  queryNextPage() {
    if (this.nextPage) this.queryMembers(this.nextPage);
  }
  

  /*=== RENDER METHODS ===*/

  // NOTE: We're using a custom template, overwriting the one in view-base.
  render() {
    return html`
      ${this.headerTemplate}

      <top-bar>
        <headline>
          <h1>q1 Network Members</h1>
          ${this.loading ? null : html`
            <span class="suffix">
              — ${this.memberCount} members
            </span>
          `}
        </headline>
        <sl-button
          id="toggle-query-settings"
          @click=${this.toggleQuerySettings}
        >
          <sl-icon
            library="material"
            name="${this.showSettings ? 'keyboard_double_arrow_left':'keyboard_double_arrow_right'}"
            slot="prefix"
          ></sl-icon>
          ${this.showSettings ? 'Hide Query Settings' : 'Show Query Settings'}
          <sl-icon library="material" name="tune" slot="suffix"></sl-icon>
        </sl-button>
      </top-bar>

      <left-bar>
        ${this.queryPanelTemplate}
      </left-bar>

      <content-container class=${classMap({
        showSettings: this.showSettings,
      })}>
        ${this.memberTableTemplate}

        <q1-spinner-panel label="Loading..." ?open=${this.loading}></q1-spinner-panel>
      </content-container>
    `;
  }

  get headerTemplate() {
    return html`
      <q1-app-header
        .currentUser="${this.currentUser}"
        .currentMember="${this.currentMember}"
        .isSignedIn="${this.isSignedIn}"
        .isAdmin="${this.isAdmin}"
        variant="flat"
      ></q1-app-header>
    `;
  }

  get queryPanelTemplate() {
    return html`
      <h2>Query Settings</h2>

      <query-pair>
        <sl-select 
          id="sortBy"
          name="sortBy"
          value=${this.sortBy}
          @sl-change=${this.handleChange}
          ?disabled=${!this.isSignedIn}
          size="small"
          filled
        >
          <span class="inner-label" slot="prefix">Sort By</span>
          <small>Sort members by:</small>
          ${SORT_BY_OPTIONS.map(({ label, value }) => html`
            <sl-option value=${value}>${label}</sl-option>
          `)}
        </sl-select>
        
        <sl-select 
          id="sortDirection"
          name="sortDirection"
          value=${this.sortDirection}
          @sl-change=${this.handleChange}
          ?disabled=${!this.isSignedIn}
          size="small"
          filled
        >
          <small>Sort direction:</small>
          ${SORT_DIRECTION_OPTIONS.map(({ label, value }) => html`
            <sl-option value=${value}>${label}</sl-option>
          `)}
        </sl-select>
      </query-pair>

      <sl-select 
        id="filterByLevels"
        name="filterByLevels"
        @sl-change=${this.handleChange}
        ?disabled=${!this.isSignedIn}
        size="small"
        placeholder="All Member Levels"
        multiple clearable filled
      >
        <span class="inner-label" slot="prefix">Level</span>
        <small>Only show members at level:</small>
        ${FILTER_BY_LEVELS_OPTIONS.map(({ label, value, icon }) => html`
          <sl-option value=${value}>
            <sl-icon library="material" name=${icon} slot="prefix"></sl-icon>
            ${label}
          </sl-option>
        `)}
      </sl-select>

      <sl-select 
        id="filterByStatus"
        name="filterByStatus"
        @sl-change=${this.handleChange}
        ?disabled=${!this.isSignedIn}
        size="small"
        placeholder="All Statuses"
        multiple clearable filled
      >
        <span class="inner-label" slot="prefix">Status</span>
        <small>Only show members with status:</small>
        ${FILTER_BY_STATUS_OPTIONS.map(({ label, value }) => html`
          <sl-option value=${value}>
            ${label}
          </sl-option>
        `)}
      </sl-select>

      <sl-divider></sl-divider>
    `;
  }

  get memberTableTemplate() {
    if (!this.isSignedIn) return html`
      <table-wrapper>
        <div class="empty-panel hero">
          <div class="inner">
            <sl-icon library="vectors" name="global-collaboration"></sl-icon>

            <h2>Network browsing is only available to members...</h2>

            <p>
              The good news is that we'd love to have you as a member!
              <strong>Interested in the future of work?</strong>
            </p>

            <sl-button
              variant="warning"
              size="large"
              href="/join"
              pill
            >
              Join the Quorum
            </sl-button>
          </div>
        </div>
      </table-wrapper>
    `;

    if (!this.members?.length) return html`
      <table-wrapper>
        <div class="empty-panel">
          <div class="inner">
            <sl-icon library="material" name="no_accounts"></sl-icon>

            <h2>Nothing to see here...</h2>

            There are no visible members in your current query.
          </span>
        </div>
      </table-wrapper>
    `;

    return html`
      <table-wrapper>
        <table id="members-table" class="q1">
          <thead>
            <th>Member</th>
            <th>Level</th>
            <th>Status</th>
            <th>Primary Email</th>
            <th>Slack</th>
            <th>Join Date</th>
          </thead>

          <tbody>
            ${this.members.map(member => html`
              <tr style=${styleMap({
                '--profile-color-primary': member.colorPrimary,
                '--profile-color-primary-10': `${member.colorPrimary}11`,
              })}>
                <td class="member-col">
                  <td-content>
                    <sl-avatar
                      class="profileImage"
                      label="${member.name}"
                      image=${member.profile || ''}
                    ></sl-avatar>
                    
                    <a href=${`/@${member.number}`} class="name-link">${member.name}</a>
                    <span class="divider">—</span>
                    <a href=${`/@${member.number}`} class="number-link">@${member.number}</a>
                    
                    <span class="action-links">
                      ${!member.linkedinProfileUrl ? '' : html`
                        <sl-icon-button
                          library="tabler"
                          name="brand-linkedin"
                          href="${member.linkedinProfileUrl}"
                          target="_blank"
                        ></sl-icon-button>
                      `}
                    </span>
                  </td-content>
                </td>
                <td class="level-col">
                  <span>
                    <sl-icon library="material" name=${member.levelIcon}></sl-icon>
                    ${member.level}
                  </span>
                </td>
                <td class="status-col">${member.status}</td>
                <td class="email-col">${member.email || '-'}</td>
                <td class="slack-col">${member.slack || '-'}</td>
                <td class="date-col">
                  ${!member.createdAt ? '-' : html`
                    <sl-format-date
                      date=${member.createdAt}
                      month="short" day="numeric" year="numeric"
                    ></sl-format-date>
                  `}
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </table-wrapper>

      ${!this.isSignedIn ? null : html`
        <table-footer>
          <page-settings>
            <sl-select 
              id="pageSizeString"
              name="pageSizeString"
              value="${this.pageSizeString}"
              @sl-change=${this.handleChange}
              filled
            >
              <span class="inner-label" slot="prefix">Page Size</span>
              <small>Members per page:</small>
              ${PAGE_SIZE_OPTIONS.map(({ label, value }) => html`
                <sl-option value=${value}>${label}</sl-option>
              `)}
            </sl-select>

            <span id="current-page-details">
              Showing ${this.firstItemIndex} - ${this.lastItemIndex}
              of ${this.querySize} matching members
            </span>
          </page-settings>
          
          <nav-buttons>
            <sl-button
              class="prior-page"
              @click="${this.queryPriorPage}"
              size="large"
              ?disabled="${!this.priorPage || this.loading}"
            >
              <sl-icon library="material" name="arrow_back" slot="prefix"></sl-icon>
              Prior <span class="hide-until-bp2">Page</span>
            </sl-button>

            <sl-button
              class="next-page"
              @click="${this.queryNextPage}"
              size="large"
              ?disabled="${!this.nextPage || this.loading}"
            >
              Next <span class="hide-until-bp2">Page</span>
              <sl-icon library="material" name="arrow_forward" slot="suffix"></sl-icon>
            </sl-button>
          </nav-buttons>
        </table-footer>
      `}
    `;
  }

}

customElements.define('admin-members-view', AdminMembersView);
